import { Link } from 'react-router-dom'

import { useCompaniesQuery } from '../../hooks/useVehicleQueries'
import { assetUrl } from '../../lib/http'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { useSection } from '../../cms/cms-context'
import { revealOnScroll } from '../../animations/gsapAnimations'

/* The badges of the companies we deal with, running past on their own.

   The marques come from the catalogue's own company records: the console
   uploads a logo once when a company is created, so this strip grows a
   marque the day the showroom takes one on and needs nothing shipped in the
   storefront. The badges are transparent PNGs and stand straight on the
   page — no plate, no card, nothing around them but the black.

   Every badge is a link into the listing below with the company already
   ticked, so picking one narrows the section it sits above rather than
   opening a page of its own. The track pauses under the pointer, because a
   logo that slides away from the cursor cannot be clicked.

   The loop is CSS, like the showroom band's: the track holds two identical
   halves and slides by exactly half its width, so the second half arrives
   where the first started and the seam never shows. */

/* The stride one badge takes up — its box plus the gap at sm and up, the
   widest the strip ever gets. */
const BADGE_STRIDE = 152 + 56

/* Wide enough to cover an ultrawide desktop with the edge fade to spare. */
const HALF_TRACK_MIN = 2400

/* Pixels a second. Slower than the stock ticker above it: badges are read at
   a glance, and a strip that hurries reads as an advert. */
const SPEED = 42

/* How often the makes repeat across the whole track, and how long one lap
   takes at that pace. A showroom carrying three marques repeats them far
   more often than one carrying fifteen, and the same seconds spent on both
   tracks would run one past as a blur and leave the other crawling. */
function trackOf(length: number) {
  const perHalf = Math.max(2, Math.ceil(HALF_TRACK_MIN / (length * BADGE_STRIDE)))
  return {
    copies: Array.from({ length: perHalf * 2 }, (_, copy) => copy),
    duration: (perHalf * length * BADGE_STRIDE) / SPEED,
  }
}

type Marque = {
  id: string
  name: string
  logo: string
}

/* One badge in the strip. The copies behind the first are decoration, so
   their links leave the tab order and the accessibility tree — a keyboard
   user meets each marque once.

   The badge is given its height in pixels rather than as a share of the box
   around it. A percentage height resolves against the row the browser laid
   out, and a row holding a 3000px logo is sized to the logo — so `h-full`
   made the image taller than the strip and the strip cropped it. A number
   cannot be argued with, and object-contain letterboxes whatever shape the
   showroom uploads inside it. */
function Badge({ marque, decorative }: { marque: Marque; decorative: boolean }) {
  return (
    <Link
      to={`/home?brand=${encodeURIComponent(marque.name)}#cars`}
      tabIndex={decorative ? -1 : undefined}
      aria-hidden={decorative || undefined}
      title={marque.name}
      className="group/badge flex w-[124px] items-center justify-center sm:w-[152px]"
    >
      {marque.logo ? (
        <img
          src={assetUrl(marque.logo)}
          alt={decorative ? '' : marque.name}
          loading="lazy"
          decoding="async"
          className="h-[72px] w-full object-contain opacity-70 transition duration-500 ease-lux group-hover/badge:scale-[1.07] group-hover/badge:opacity-100 sm:h-[84px]"
        />
      ) : (
        /* A make nobody has given a badge yet runs as its own name in the
           display face rather than as a torn image icon. */
        <span className="font-display text-[22px] leading-none font-bold tracking-[-0.02em] text-steel transition-colors duration-500 group-hover/badge:text-platinum">
          {marque.name}
        </span>
      )}
    </Link>
  )
}

/* Placeholders keep the strip's height while the catalogue loads, so the
   page does not jump when the badges land. */
function BadgeSkeleton() {
  return (
    <div className="flex w-max items-center gap-10 sm:gap-14" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, index) => (
        <span
          key={index}
          className="h-[72px] w-[124px] shrink-0 animate-pulse rounded-xl bg-white/[0.04] sm:h-[84px] sm:w-[152px]"
        />
      ))}
    </div>
  )
}

function Companies() {
  const cms = useSection()
  const { data: companies = [], isPending: loading } = useCompaniesQuery()

  /* Every company the console holds, whether or not one of its cars is
     standing on the floor today. The strip is a statement about who the
     showroom deals with, not a summary of the stock — a marque the
     showroom represents belongs here on the morning its last car sells. */
  const marques: Marque[] = companies.map((company) => ({
    id: company.id,
    name: company.name,
    logo: company.logo,
  }))

  /* The line fades up, then the strip rolls in from the edge it is already
     travelling from, before the marquee takes over. The slide is on a frame
     around the track, never the track itself: that transform belongs to the
     CSS loop. Keyed on the marque count because the badges arrive with the
     catalogue. */
  const ref = useGsapContext<HTMLElement>(
    (scope) => {
      const label = scope.querySelector('[data-label]')
      if (label) revealOnScroll(label, { y: 16, start: 'top 92%' })

      const track = scope.querySelector('[data-track]')
      if (track) revealOnScroll(track, { x: 72, y: 0, duration: 1.2, start: 'top 94%' })
    },
    [loading ? 0 : marques.length],
  )

  /* A console with no companies recorded yet leaves the page to the listing
     below, which says so properly. */
  if (!loading && marques.length === 0) return null

  const { copies, duration } = trackOf(Math.max(marques.length, 1))

  return (
    <section
      ref={ref}
      aria-labelledby="companies-title"
      className="group relative overflow-hidden py-16 sm:py-20"
    >
      <div className="mx-auto w-full max-w-[1320px] px-5 sm:px-8">
        <h2
          id="companies-title"
          data-label
          {...cms.field('title')}
          className="text-center label text-steel"
        >
          {cms.text('title', 'Companies we deal with')}
        </h2>
      </div>

      <div className="mt-10 overflow-hidden edge-fade">
        {loading ? (
          <BadgeSkeleton />
        ) : (
          <div data-track>
            {/* The row carries a little height over the badges themselves: a
                badge grows under the pointer, and a track cut exactly to its
                own height would shave the top and bottom off it. */}
            <ul
              style={{ animationDuration: `${duration.toFixed(1)}s` }}
              className="flex w-max animate-marquee items-center gap-10 py-3 sm:gap-14 group-hover:[animation-play-state:paused]"
            >
              {copies.map((copy) =>
                marques.map((marque) => (
                  <li key={`${copy}-${marque.id}`} className="shrink-0">
                    <Badge marque={marque} decorative={copy > 0} />
                  </li>
                )),
              )}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}

export default Companies
